import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumCustomerHappinessRating } from "./enums/enumCustomerHappinessRating.generated";

// Model Objects

export class SupplierManufacturerCommunicationLogEntry extends ModelObject<SupplierManufacturerCommunicationLogEntry> {

    public id: Guid = Guid.createEmpty();
    /**
    * Id of the communication log's communication subject.
    */
    public communicationSubjectId: Guid = Guid.createEmpty();
    /**
    * Id of the communication log's communication type.
    */
    public communicationTypeId: Guid = Guid.createEmpty();
    /**
    * Rating of the customer happiness.
    */
    public customerHappinessRating: EnumCustomerHappinessRating = EnumCustomerHappinessRating.neutral;
    /**
    * Description of the communication log entry.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public description: string = "";
    /**
    * Supplier/Manufacturer relating to which the communication occurred.
    */
    public supplierManufacturerId: Guid = Guid.createEmpty();
    /**
    * Timestamp at which the communication occurred.
    */
    public timestamp: Instant = Instant.ofEpochMilli(0);
    /**
    * User who created the communication log entry.
    */
    public userId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.communicationSubjectId = Guid.parseOrEmpty(jsonSerializedObject.communicationSubjectId);
          this.communicationTypeId = Guid.parseOrEmpty(jsonSerializedObject.communicationTypeId);
          this.customerHappinessRating = EnumCustomerHappinessRating.getByValue(jsonSerializedObject.customerHappinessRating, this.customerHappinessRating);
          this.description = jsonSerializedObject.description;
          this.supplierManufacturerId = Guid.parseOrEmpty(jsonSerializedObject.supplierManufacturerId);
          this.timestamp = Instant.parse(jsonSerializedObject.timestamp);
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.communicationSubjectId = this.communicationSubjectId.toString();
        toRet.communicationTypeId = this.communicationTypeId.toString();
        toRet.customerHappinessRating = this.customerHappinessRating.value;
        toRet.description = this.description;
        toRet.supplierManufacturerId = this.supplierManufacturerId.toString();
        toRet.timestamp = this.timestamp.toString();
        toRet.userId = this.userId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "SupplierManufacturerCommunicationLogEntry.Id cannot be empty"},
            {field: "communicationSubjectId", rule: (v: any) => !v.isEmpty() || "SupplierManufacturerCommunicationLogEntry.CommunicationSubjectId cannot be empty"},
            {field: "communicationTypeId", rule: (v: any) => !v.isEmpty() || "SupplierManufacturerCommunicationLogEntry.CommunicationTypeId cannot be empty"},
            {field: "supplierManufacturerId", rule: (v: any) => !v.isEmpty() || "SupplierManufacturerCommunicationLogEntry.SupplierManufacturerId cannot be empty"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "SupplierManufacturerCommunicationLogEntry.UserId cannot be empty"},
    ];
}


export class SupplierManufacturerLogEntryFilter extends ModelObject<SupplierManufacturerLogEntryFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _supplierManufacturerIds: readonly (Guid)[] | null = null;
    public get supplierManufacturerIds(): readonly (Guid)[] | null { return this._supplierManufacturerIds }
    public set supplierManufacturerIds(value: readonly (Guid)[] | null) {
      this._supplierManufacturerIds = value === undefined ? null : value;
    }
    private _userIds: readonly (Guid)[] | null = null;
    public get userIds(): readonly (Guid)[] | null { return this._userIds }
    public set userIds(value: readonly (Guid)[] | null) {
      this._userIds = value === undefined ? null : value;
    }
    private _fromTimestamp: Instant | null = null;
    /**
    * Minimum (inclusive) timestamp.
    */
    public get fromTimestamp(): Instant | null { return this._fromTimestamp }
    public set fromTimestamp(value: Instant | null) {
      this._fromTimestamp = value === undefined ? null : value;
    }
    private _toTimestamp: Instant | null = null;
    /**
    * Maximum (inclusive) timestamp.
    */
    public get toTimestamp(): Instant | null { return this._toTimestamp }
    public set toTimestamp(value: Instant | null) {
      this._toTimestamp = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.supplierManufacturerIds = jsonSerializedObject.supplierManufacturerIds == null ? null : jsonSerializedObject.supplierManufacturerIds.map((v: any) => Guid.parseOrEmpty(v));
          this.userIds = jsonSerializedObject.userIds == null ? null : jsonSerializedObject.userIds.map((v: any) => Guid.parseOrEmpty(v));
          this.fromTimestamp = jsonSerializedObject.fromTimestamp == null ? null : Instant.parse(jsonSerializedObject.fromTimestamp);
          this.toTimestamp = jsonSerializedObject.toTimestamp == null ? null : Instant.parse(jsonSerializedObject.toTimestamp);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.supplierManufacturerIds = this.supplierManufacturerIds == null ? null : this.supplierManufacturerIds.map(v => v.toString());
        toRet.userIds = this.userIds == null ? null : this.userIds.map(v => v.toString());
        toRet.fromTimestamp = this.fromTimestamp == null ? null : this.fromTimestamp.toString();
        toRet.toTimestamp = this.toTimestamp == null ? null : this.toTimestamp.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredSupplierManufacturerLogEntryResults extends ModelObject<FilteredSupplierManufacturerLogEntryResults> {

    public matchingCount: number = 0;
    public supplierManufacturerLogEntries: (SupplierManufacturerCommunicationLogEntry)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.supplierManufacturerLogEntries = jsonSerializedObject.supplierManufacturerLogEntries.map((v: any) => new SupplierManufacturerCommunicationLogEntry(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.supplierManufacturerLogEntries = this.supplierManufacturerLogEntries.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "supplierManufacturerLogEntries", rule: (v: any) => !!v || "supplierManufacturerLogEntries is required"},
            {field: "supplierManufacturerLogEntries", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SupplierManufacturerCommunicationLogEntry[]).reduce((acc: Array<string>, curr: SupplierManufacturerCommunicationLogEntry) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class SupplierManufacturerCommunicationLogService extends ServiceBase {

  public async addSupplierManufacturerLogEntry(supplierManufacturerCommunicationLogEntry: SupplierManufacturerCommunicationLogEntry, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SupplierManufacturerCommunicationLogEntry> {
    const req = {
    tenantId:api.tenantId.toString(),
    supplierManufacturerCommunicationLogEntry:supplierManufacturerCommunicationLogEntry.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new SupplierManufacturerCommunicationLogEntry(response.supplierManufacturerLogEntry);;
    return this.makeJsonRequest("supplierManufacturerCommunicationLog/v1/addSupplierManufacturerLogEntry", req, true, extractor, opts);
  };

  public async getSupplierManufacturerEntries(supplierManufacturerLogEntryIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SupplierManufacturerCommunicationLogEntry)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    supplierManufacturerLogEntryIds:supplierManufacturerLogEntryIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.supplierManufacturerLogEntries.map((v: any) => new SupplierManufacturerCommunicationLogEntry(v));;
    return this.makeJsonRequest("supplierManufacturerCommunicationLog/v1/getSupplierManufacturerEntries", req, false, extractor, opts);
  };

  public async getSupplierManufacturerLogEntriesByFilter(filter: SupplierManufacturerLogEntryFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredSupplierManufacturerLogEntryResults> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredSupplierManufacturerLogEntryResults(response.results);;
    return this.makeJsonRequest("supplierManufacturerCommunicationLog/v1/getSupplierManufacturerLogEntriesByFilter", req, false, extractor, opts);
  };
}

export const supplierManufacturerCommunicationLogService = new SupplierManufacturerCommunicationLogService();